export default function componentStyleOverrides(theme) {
    const isDark = theme?.mode === 'dark';
    const inputBg = theme.inputBackground;
    const inputBorder = theme.inputBorder;
    const inputBorderHover = theme.inputBorderHover;

    return {
        MuiButton: {
            styleOverrides: {
                root: {
                    fontWeight: 500,
                    borderRadius: '4px'
                }
            }
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    backgroundColor: theme.paper,
                    color: theme.textDark,
                },
                rounded: {
                    borderRadius: `${theme?.customization?.borderRadius}px`
                }
            }
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: theme.paper,
                    color: theme.textDark,
                    boxShadow: theme.cardShadow,
                    border: isDark ? `1px solid ${theme.divider}` : 'none',
                }
            }
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    color: theme.textDark,
                    padding: '24px'
                },
                title: {
                    fontSize: '1.125rem'
                }
            }
        },
        MuiCardContent: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiCardActions: {
            styleOverrides: {
                root: {
                    padding: '24px'
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: theme.paper,
                    color: theme.textDark,
                    backgroundImage: 'none',
                }
            }
        },
        MuiDrawer: {
            styleOverrides: {
                paper: {
                    backgroundColor: theme.paper,
                    backgroundImage: 'none',
                }
            }
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    backgroundColor: theme.paper,
                    backgroundImage: 'none',
                }
            }
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: theme.paper,
                    backgroundImage: 'none',
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    backgroundColor: theme.paper,
                    backgroundImage: 'none',
                }
            }
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    color: theme.darkTextPrimary,
                    paddingTop: '10px',
                    paddingBottom: '10px',
                    '&.Mui-selected': {
                        color: theme.menuSelected,
                        backgroundColor: theme.menuSelectedBack,
                        '&:hover': {
                            backgroundColor: theme.menuSelectedBack
                        },
                        '& .MuiListItemIcon-root': {
                            color: theme.menuSelected
                        }
                    },
                    '&:hover': {
                        backgroundColor: theme.menuSelectedBack,
                        color: theme.menuSelected,
                        '& .MuiListItemIcon-root': {
                            color: theme.menuSelected
                        }
                    }
                }
            }
        },
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    color: theme.darkTextPrimary,
                    minWidth: '36px'
                }
            }
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: theme.textDark
                }
            }
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    color: theme.textDark,
                    '&::placeholder': {
                        color: theme.darkTextSecondary,
                        fontSize: '0.875rem',
                        opacity: 1,
                    }
                }
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    background: inputBg,
                    color: theme.textDark,
                    borderRadius: `${theme?.customization?.borderRadius}px`,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: inputBorder,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: inputBorderHover,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.colors?.primaryMain,
                        borderWidth: '1.5px',
                    },
                    '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.divider,
                    },
                    '&.MuiInputBase-multiline': {
                        padding: 1,
                    }
                },
                input: {
                    fontWeight: 500,
                    background: 'transparent',
                    color: theme.textDark,
                    padding: '15.5px 14px',
                    borderRadius: `${theme?.customization?.borderRadius}px`,
                    '&.MuiInputBase-inputSizeSmall': {
                        padding: '10px 14px',
                        '&.MuiInputBase-inputAdornedStart': {
                            paddingLeft: 0
                        }
                    }
                },
                inputAdornedStart: {
                    paddingLeft: 4
                },
                notchedOutline: {
                    borderRadius: `${theme?.customization?.borderRadius}px`
                }
            }
        },
        MuiFilledInput: {
            styleOverrides: {
                root: {
                    backgroundColor: inputBg,
                    color: theme.textDark,
                }
            }
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    color: theme.darkTextSecondary,
                    '&.Mui-focused': {
                        color: theme.colors?.primaryMain,
                    },
                    '&.Mui-error': {
                        color: theme.colors?.errorMain,
                    }
                }
            }
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    color: theme.darkTextSecondary,
                    '&.Mui-error': {
                        color: theme.colors?.errorMain,
                    }
                }
            }
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: theme.darkTextSecondary,
                    '&.Mui-focused': {
                        color: theme.colors?.primaryMain,
                    }
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    color: theme.darkTextSecondary,
                }
            }
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    color: theme.textDark,
                    '&:hover': {
                        backgroundColor: theme.hoverSurface,
                    },
                    '&.Mui-selected': {
                        backgroundColor: theme.menuSelectedBack,
                        color: theme.menuSelected,
                        '&:hover': {
                            backgroundColor: theme.menuSelectedBack,
                        }
                    }
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: theme.darkTextSecondary,
                    '&.Mui-checked': {
                        color: theme.colors?.primaryMain,
                    }
                }
            }
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: theme.darkTextSecondary,
                    '&.Mui-checked': {
                        color: theme.colors?.primaryMain,
                    }
                }
            }
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    '& .MuiSwitch-track': {
                        backgroundColor: theme.darkTextSecondary,
                        opacity: 0.4,
                    },
                    '& .MuiSwitch-thumb': {
                        backgroundColor: isDark ? theme.colors?.grey50 : '#fff',
                    }
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    color: theme.textDark,
                    borderBottomColor: theme.divider,
                },
                head: {
                    color: theme.textDark,
                    fontWeight: 600,
                    backgroundColor: theme.hoverSurface,
                }
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: theme.hoverSurface,
                    }
                }
            }
        },
        MuiSlider: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        color: theme.colors?.grey300
                    }
                },
                mark: {
                    backgroundColor: theme.paper,
                    width: '4px'
                },
                valueLabel: {
                    color: theme?.colors?.primaryLight
                }
            }
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: theme.divider,
                    opacity: 1
                }
            }
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    color: theme.colors?.primaryDark,
                    background: theme.colors?.primary200
                }
            }
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    '&.MuiChip-deletable .MuiChip-deleteIcon': {
                        color: 'inherit'
                    }
                }
            }
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    color: isDark ? theme.colors?.grey900 : theme.paper,
                    background: isDark ? theme.colors?.grey100 : theme.colors?.grey700,
                    fontSize: '0.75rem',
                },
                arrow: {
                    color: isDark ? theme.colors?.grey100 : theme.colors?.grey700,
                }
            }
        }
    };
}
